import {
  array,
  boolean as booleanType,
  date,
  Describe,
  nullable,
  number,
  object,
  optional,
  string,
} from 'superstruct';

import {
  AugmentedCreateCrtTrustInput,
  AugmentedCreateEntityInput,
  AugmentedUpdateCrtTrustInput,
  AugmentedUpdateEntityInput,
  CreateCrtTrustInput,
  UpdateCrtTrustInput,
} from '@/types/schema';

import {
  createInsurancePolicyInput,
  updateInsurancePolicyInput,
} from './insuranceTypes';
import {
  beneficiarySchema,
  commonUpdateEntityProperties,
  createAccountSchema,
  createEntityInput,
  decimal,
  grantorSchema,
  gstStatusSchema,
  inclusionRatioSchema,
  inEstateStatusSchema,
  stageSchema,
  taxStatusSchema,
  trustAdvisorSchema,
  trusteeSchema,
  updateAccountSchema,
} from './types';
import { $getValidatedInput } from './utils';

const crtCreateUpdateProperties = {
  // share trust properties
  legalName: optional(nullable(string())),
  trustJurisdictionStateCode: optional(nullable(string())),
  effectiveDate: optional(nullable(date())),
  description: optional(nullable(string())),
  taxStatus: taxStatusSchema,
  inEstateStatus: inEstateStatusSchema,
  stateInEstateStatus: inEstateStatusSchema,
  gstStatus: gstStatusSchema,
  inclusionRatio: inclusionRatioSchema,

  // CRT-specific properties
  termDurationYears: optional(nullable(number())),
  officialInterestRatePercent: optional(nullable(decimal)),
  charitableDeduction: optional(nullable(decimal)),
  termDurationLifetime: optional(nullable(booleanType())),
  householdID: optional(nullable(string())),
};

const createCrtTrustInput: Describe<CreateCrtTrustInput> = object({
  displayName: string(),
  ...crtCreateUpdateProperties,
  householdID: string(),
});

const createCrtTrustSchema: Describe<AugmentedCreateCrtTrustInput> = object({
  create: createCrtTrustInput,
  withLifetimeBeneficiaries: optional(nullable(array(beneficiarySchema))),
  withRemainderBeneficiaries: optional(nullable(array(beneficiarySchema))),
  withTrustees: optional(nullable(array(trusteeSchema))),
  withSuccessorTrustees: optional(nullable(array(trusteeSchema))),
  withTrustAdvisors: optional(nullable(array(trustAdvisorSchema))),
  withGrantors: optional(nullable(array(grantorSchema))),
  withDesignerAccount: optional(nullable(createAccountSchema)),
});

const updateCrtTrustInput: Describe<UpdateCrtTrustInput> = object({
  displayName: optional(nullable(string())),
  ...crtCreateUpdateProperties,
  clearEffectiveDate: optional(nullable(booleanType())),
  clearLegalName: optional(nullable(booleanType())),
  clearTrustJurisdictionStateCode: optional(nullable(booleanType())),
  clearLifetimeBeneficiaries: optional(nullable(booleanType())),
  clearRemainderBeneficiaries: optional(nullable(booleanType())),
  clearGrantors: optional(nullable(booleanType())),
  clearSuccessorTrustees: optional(nullable(booleanType())),
  clearTrustAdvisors: optional(nullable(booleanType())),
  clearDescription: optional(nullable(booleanType())),
  clearTrustees: optional(nullable(booleanType())),
  clearGstStatus: optional(nullable(booleanType())),
  clearTaxStatus: optional(nullable(booleanType())),
  clearInclusionRatio: optional(nullable(booleanType())),
  removePolicyIDs: optional(nullable(array(string()))),
});

const updateCrtTrustSchema: Describe<AugmentedUpdateCrtTrustInput> = object({
  id: string(),
  update: updateCrtTrustInput,
  withLifetimeBeneficiaries: optional(nullable(array(beneficiarySchema))),
  withRemainderBeneficiaries: optional(nullable(array(beneficiarySchema))),
  withTrustees: optional(nullable(array(trusteeSchema))),
  withSuccessorTrustees: optional(nullable(array(trusteeSchema))),
  withTrustAdvisors: optional(nullable(array(trustAdvisorSchema))),
  withGrantors: optional(nullable(array(grantorSchema))),
  updateDesignerAccount: optional(nullable(updateAccountSchema)),
  withPolicies: optional(nullable(array(createInsurancePolicyInput))),
  updatePolicies: optional(nullable(array(updateInsurancePolicyInput))),
});

const createSchema: Describe<AugmentedCreateEntityInput> = object({
  create: createEntityInput,
  withCrtTrust: optional(nullable(createCrtTrustSchema)),
});

const updateSchema: Describe<AugmentedUpdateEntityInput> = object({
  id: string(),
  update: object({
    ...commonUpdateEntityProperties,
    stage: optional(nullable(stageSchema)),
  }),
  updateCrtTrust: optional(nullable(updateCrtTrustSchema)),
});

export function validateCrtTrustCreateInput(
  input: AugmentedCreateEntityInput
): AugmentedCreateEntityInput {
  return $getValidatedInput(input, createSchema, 'CRT create');
}

export function validateCrtTrustUpdateInput(
  input: AugmentedUpdateEntityInput
): AugmentedUpdateEntityInput {
  return $getValidatedInput(input, updateSchema, 'CRT update');
}
